import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Download, FileDown, RefreshCw, Loader2, Plus, Trash2, Copy, ArrowUp, ArrowDown, Sparkles } from "lucide-react";
import { listAdminPosts } from "@/lib/posts.functions";
import {
  listEkonomimColumns,
  getCarouselSource,
  generateAiCarouselSlides,
  type CarouselSource,
} from "@/lib/carousel.functions";
import {
  SlidePreview,
  SlideEditor,
  useSlideDownloader,
  useAutoSlides,
  CAROUSEL_THEMES,
  renderSlide,
  type Slide,
} from "@/components/CarouselBuilder";

type ThemeName = (typeof CAROUSEL_THEMES)[number];


export const Route = createFileRoute("/admin/carousel")({
  head: () => ({
    meta: [
      { title: "LinkedIn Carousel — Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: CarouselPage,
});

type Kind = "post" | "ekonomim";

function CarouselPage() {
  const [kind, setKind] = useState<Kind>("post");
  const [selected, setSelected] = useState<{ kind: Kind; id?: string; url?: string; label: string } | null>(null);
  const [source, setSource] = useState<CarouselSource | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slides, setSlides] = useState<Slide[]>([]);
  const [dirtySource, setDirtySource] = useState(0);
  const [theme, setTheme] = useState<ThemeName>("editorial");


  const fetchPosts = useServerFn(listAdminPosts);
  const fetchEkonomim = useServerFn(listEkonomimColumns);
  const loadSource = useServerFn(getCarouselSource);
  const aiGenerate = useServerFn(generateAiCarouselSlides);
  const download = useSlideDownloader();
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  const posts = useQuery({ queryKey: ["carousel-posts"], queryFn: () => fetchPosts() });
  const eko = useQuery({
    queryKey: ["carousel-ekonomim"],
    queryFn: () => fetchEkonomim(),
    enabled: kind === "ekonomim",
    staleTime: 5 * 60 * 1000,
  });

  const auto = useAutoSlides(source);
  // Sync auto slides into editable state whenever a new source loads.
  const syncKey = `${selected?.id ?? selected?.url ?? "-"}::${dirtySource}`;
  const lastSyncKey = useRef<string>("");
  useEffect(() => {
    if (auto.length > 0 && lastSyncKey.current !== syncKey) {
      lastSyncKey.current = syncKey;
      setSlides(auto);
    }
  }, [auto, syncKey]);

  async function pick(sel: { kind: Kind; id?: string; url?: string; label: string }) {
    setSelected(sel);
    setLoading(true);
    setError(null);
    setSource(null);
    try {
      const s = await loadSource({ data: { kind: sel.kind, id: sel.id, url: sel.url } });
      setSource(s);
      setDirtySource((v) => v + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Yükleme hatası");
    } finally {
      setLoading(false);
    }
  }

  const filenameBase = slugify(source?.title ?? "carousel");

  function downloadAll() {
    slides.forEach((s, i) => {
      setTimeout(() => download(s, i, slides.length, filenameBase, theme), i * 250);
    });
  }

  const [pdfBusy, setPdfBusy] = useState(false);
  async function downloadPdf() {
    if (slides.length === 0) return;
    setPdfBusy(true);
    try {
      const { jsPDF } = await import("jspdf");
      // 1080x1350 px slides. Use pt units so LinkedIn accepts the doc.
      const pdf = new jsPDF({ unit: "px", format: [1080, 1350], orientation: "portrait", compress: true });
      const canvas = document.createElement("canvas");
      for (let i = 0; i < slides.length; i++) {
        renderSlide(canvas, slides[i], i, slides.length, slides[i].theme ?? theme);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
        if (i > 0) pdf.addPage([1080, 1350], "portrait");
        pdf.addImage(dataUrl, "JPEG", 0, 0, 1080, 1350);
        // yield to UI
        await new Promise((r) => setTimeout(r, 0));
      }
      pdf.save(`${filenameBase}-linkedin.pdf`);
    } finally {
      setPdfBusy(false);
    }
  }

  function updateSlide(i: number, next: Slide) {
    setSlides((arr) => arr.map((x, idx) => (idx === i ? next : x)));
  }
  function removeSlide(i: number) {
    setSlides((arr) => arr.filter((_, idx) => idx !== i));
  }
  function duplicateSlide(i: number) {
    setSlides((arr) => [...arr.slice(0, i + 1), { ...arr[i] }, ...arr.slice(i + 1)]);
  }
  function moveSlide(i: number, dir: -1 | 1) {
    setSlides((arr) => {
      const j = i + dir;
      if (j < 0 || j >= arr.length) return arr;
      const copy = arr.slice();
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
  }
  function addSlide(after?: number) {
    const blank: Slide = {
      kind: "content",
      eyebrow: "Not",
      title: "Yeni başlık",
      body: "Buraya metnini yaz.\n\nBoş satır bırakırsan yeni paragraf olur.",
    };
    setSlides((arr) => {
      if (after === undefined) return [...arr, blank];
      return [...arr.slice(0, after + 1), blank, ...arr.slice(after + 1)];
    });
  }

  async function runAi() {
    if (!source) return;
    setAiBusy(true);
    setAiError(null);
    try {
      const res = await aiGenerate({
        data: {
          title: source.title,
          excerpt: source.excerpt,
          paragraphs: source.paragraphs,
          sourceLabel: source.sourceLabel,
          slideCount: Math.max(5, Math.min(12, slides.length || 9)),
        },
      });
      setSlides(res.slides as Slide[]);
    } catch (e) {
      setAiError(e instanceof Error ? e.message : "AI hata verdi");
    } finally {
      setAiBusy(false);
    }
  }



  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-serif text-2xl">LinkedIn Carousel Builder</h1>
        <p className="text-sm text-ink/60 mt-1">
          Yazılarımdan standart formatta LinkedIn carousel slaytları üret. 1080×1350 PNG olarak
          tek tek indir, LinkedIn belge yükleme akışına ekle.
        </p>
      </header>

      {/* Source picker */}
      <section className="border border-border rounded">
        <div className="flex border-b border-border">
          {(["post", "ekonomim"] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setKind(k)}
              className={`px-4 py-2 text-xs uppercase tracking-[0.15em] border-r border-border ${
                kind === k ? "bg-ink text-paper" : "text-ink/60 hover:text-ink"
              }`}
            >
              {k === "post" ? "Site yazıları" : "Ekonomim"}
            </button>
          ))}
        </div>
        <div className="max-h-72 overflow-y-auto">
          {kind === "post" && (
            <ul className="divide-y divide-border">
              {(posts.data?.posts ?? []).map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() =>
                      pick({ kind: "post", id: p.id, label: p.title_tr || p.slug })
                    }
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-ink/5 flex items-center justify-between gap-3 ${
                      selected?.id === p.id ? "bg-ink/5" : ""
                    }`}
                  >
                    <span className="truncate">{p.title_tr || p.slug}</span>
                    <span className="text-[10px] uppercase tracking-[0.15em] text-ink/40 shrink-0">
                      {p.published ? "yayında" : "taslak"}
                    </span>
                  </button>
                </li>
              ))}
              {posts.isLoading && <li className="px-4 py-3 text-xs text-ink/60">Yükleniyor...</li>}
            </ul>
          )}
          {kind === "ekonomim" && (
            <ul className="divide-y divide-border">
              {eko.isLoading && <li className="px-4 py-3 text-xs text-ink/60">Ekonomim listesi çekiliyor...</li>}
              {eko.error && <li className="px-4 py-3 text-xs text-clay">Hata: {(eko.error as Error).message}</li>}
              {(eko.data ?? []).map((it) => (
                <li key={it.url}>
                  <button
                    type="button"
                    onClick={() => pick({ kind: "ekonomim", url: it.url, label: it.title })}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-ink/5 flex items-center justify-between gap-3 ${
                      selected?.url === it.url ? "bg-ink/5" : ""
                    }`}
                  >
                    <span className="truncate">{it.title}</span>
                    <span className="text-[10px] uppercase tracking-[0.15em] text-ink/40 shrink-0">
                      {it.date}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {loading && (
        <div className="text-sm text-ink/60 inline-flex items-center gap-2">
          <Loader2 className="animate-spin" size={14} /> İçerik yükleniyor…
        </div>
      )}
      {error && <p className="text-sm text-clay">{error}</p>}

      {source && slides.length > 0 && (
        <>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-[0.15em] text-ink/50">Kaynak</p>
              <p className="font-serif text-lg">{source.title}</p>
              <a href={source.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] text-clay underline">
                {source.sourceLabel}
              </a>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={theme}
                onChange={(e) => setTheme(e.target.value as ThemeName)}
                className="text-xs border border-border rounded-full px-3 py-1.5 bg-paper"
                title="Genel tema"
              >
                {CAROUSEL_THEMES.map((t) => (
                  <option key={t} value={t}>Tema: {t}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={runAi}
                disabled={aiBusy}
                className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 border border-clay text-clay rounded-full hover:bg-clay hover:text-paper disabled:opacity-60"
                title="Yazının kritik noktalarını AI ile çıkarıp slaytlara yaz"
              >
                {aiBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                {aiBusy ? "AI çalışıyor…" : "AI ile akıllı üret"}
              </button>
              <button
                type="button"
                onClick={() => setDirtySource((v) => v + 1)}
                className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 border border-border rounded-full hover:bg-ink hover:text-paper"
                title="Basit paragraf-tabanlı otomatik üretim"
              >
                <RefreshCw size={12} /> Basit otomatik
              </button>
              <button
                type="button"
                onClick={downloadAll}
                className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-ink/20 hover:bg-ink hover:text-paper uppercase tracking-[0.15em]"
              >
                <Download size={12} /> PNG olarak indir
              </button>
              <button
                type="button"
                onClick={downloadPdf}
                disabled={pdfBusy}
                className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-ink text-paper uppercase tracking-[0.15em] disabled:opacity-60"
              >
                {pdfBusy ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />}
                {pdfBusy ? "PDF hazırlanıyor…" : "PDF indir (LinkedIn)"}
              </button>
            </div>
          </div>
          {aiError && <p className="text-sm text-clay">AI: {aiError}</p>}



          <div className="grid md:grid-cols-2 gap-4">
            {slides.map((s, i) => (
              <div key={i} className="space-y-2">
                <SlidePreview
                  slide={s}
                  index={i}
                  total={slides.length}
                  theme={theme}
                  onDownload={() => download(s, i, slides.length, filenameBase, theme)}
                />
                <div className="flex items-center gap-1 flex-wrap">
                  <button type="button" onClick={() => moveSlide(i, -1)} disabled={i === 0}
                    className="text-[11px] inline-flex items-center gap-1 px-2 py-1 border border-border rounded hover:bg-ink hover:text-paper disabled:opacity-40">
                    <ArrowUp size={11} /> Yukarı
                  </button>
                  <button type="button" onClick={() => moveSlide(i, 1)} disabled={i === slides.length - 1}
                    className="text-[11px] inline-flex items-center gap-1 px-2 py-1 border border-border rounded hover:bg-ink hover:text-paper disabled:opacity-40">
                    <ArrowDown size={11} /> Aşağı
                  </button>
                  <button type="button" onClick={() => duplicateSlide(i)}
                    className="text-[11px] inline-flex items-center gap-1 px-2 py-1 border border-border rounded hover:bg-ink hover:text-paper">
                    <Copy size={11} /> Çoğalt
                  </button>
                  <button type="button" onClick={() => addSlide(i)}
                    className="text-[11px] inline-flex items-center gap-1 px-2 py-1 border border-border rounded hover:bg-ink hover:text-paper">
                    <Plus size={11} /> Sonrasına ekle
                  </button>
                  <button type="button" onClick={() => removeSlide(i)}
                    className="text-[11px] inline-flex items-center gap-1 px-2 py-1 border border-clay text-clay rounded hover:bg-clay hover:text-paper ml-auto">
                    <Trash2 size={11} /> Sil
                  </button>
                </div>
                <SlideEditor
                  slide={s}
                  onChange={(next) => updateSlide(i, next)}
                />
              </div>
            ))}
          </div>

          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => addSlide()}
              className="text-xs inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-dashed border-ink/30 hover:bg-ink hover:text-paper"
            >
              <Plus size={12} /> Slayt ekle ({slides.length} slayt)
            </button>
          </div>


          <div className="border border-border rounded p-4 text-xs text-ink/70 leading-relaxed">
            <p className="font-medium text-ink mb-1">LinkedIn'e yükleme</p>
            <ol className="list-decimal ml-4 space-y-1">
              <li>"PDF indir (LinkedIn)" ile tüm slaytları sıralı tek PDF olarak indir.</li>
              <li>LinkedIn "Yeni gönderi" → "Belge ekle" → indirdiğin PDF'i seç.</li>
              <li>Kapak yazı metnini gönderi açıklamasına yapıştır.</li>
            </ol>
            <p className="mt-2 text-ink/50">Tek tek PNG istersen "PNG olarak indir" butonunu kullan.</p>
          </div>
        </>
      )}
    </div>
  );
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60) || "carousel";
}

